import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DEFAULT_PHONE_NUMBER_VALUE, PHONE_NUMBER_MASK } from '../../constants';
import { ContactInfoDto } from '../../dtos/contact-info.dto';
import { InputCapitalizeDirective } from '../../directives/input-capitalize.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import { TriggerSubmitOnEnterDirective } from '../../directives/trigger-submit-on-enter.directive';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';

class ContactInfoForm implements Record<keyof ContactInfoDto, FormControl<string>> {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  middleName: FormControl<string>;
  phoneNumber: FormControl<string>;
}

@Component({
  selector: 'contact-info',
  templateUrl: './contact-info.component.html',
  styleUrls: ['./contact-info.component.scss'],
  imports: [ReactiveFormsModule, InputCapitalizeDirective, TranslatePipe, NgxMaskDirective, TriggerSubmitOnEnterDirective],
})
export class ContactInfoComponent implements OnChanges, OnInit {
  private formBuilder = inject(FormBuilder);
  private scrollToService = inject(ScrollToService);
  private elementRef = inject(ElementRef);

  readonly isMiddleNameRequired = input<boolean>(false);
  readonly inputList = input<('names' | 'phone')[]>(['names', 'phone']);
  readonly contactInfo = input.required<ContactInfoDto>();
  readonly formSubmitted = output<void>();

  contactForm: FormGroup<ContactInfoForm>;
  private isFormErrorsVisible = signal<boolean>(false);

  constructor() {
    effect(() => {
      const isMiddleNameRequired = this.isMiddleNameRequired();
      if (isMiddleNameRequired) {
        this.contactForm.controls.middleName.setValidators(Validators.required);
      } else {
        this.contactForm.controls.middleName.clearValidators();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.contactInfo?.currentValue) {
      this.buildContactInfoForm(this.contactInfo());
    }
  }

  ngOnInit(): void {
  }

  private buildContactInfoForm(contactInfo: ContactInfoDto) {
    this.contactForm = this.formBuilder.group({
      firstName: this.formBuilder.control(contactInfo.firstName, Validators.required),
      lastName: this.formBuilder.control(contactInfo.lastName, Validators.required),
      middleName: this.formBuilder.control(contactInfo.middleName, this.isMiddleNameRequired() ? Validators.required : null),
      phoneNumber: this.formBuilder.control(contactInfo.phoneNumber || DEFAULT_PHONE_NUMBER_VALUE, Validators.required),
    });
  }

  protected isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  protected getLabelText(control: FormControl): string {
    const controlName: keyof ContactInfoForm = Object.keys(control.parent.controls).find(key => {
      return control === control.parent.get(key);
    }) as keyof ContactInfoForm;

    switch (controlName) {
      case 'firstName':
        return 'global.name';
      case 'lastName':
        return 'global.last_name';
      case 'middleName':
        return 'global.parent_name';
    }
  }

  checkValidity(): boolean {
    const isValid = this.isValid();
    if (!isValid) {
      this.isFormErrorsVisible.set(true);
      this.scrollToService.scrollTo({ target: this.elementRef, offset: -50 });
    }

    return isValid;
  }

  isValid(): boolean {
    const isNamesValid = this.contactForm.controls.firstName.valid
      && this.contactForm.controls.lastName.valid
      && this.contactForm.controls.middleName.valid;
    const isPhoneValid = this.contactForm.controls.phoneNumber.valid;

    if (this.inputList().includes('names') && !isNamesValid) {
      return false;
    }

    if (this.inputList().includes('phone') && !isPhoneValid) {
      return false;
    }

    return true;
  }

  getValue(): ContactInfoDto {
    return this.contactForm.getRawValue();
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
